import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LinkIcon from "@mui/icons-material/Link";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import {
  resellingAccountStatusAction,
  resellingConnectAccountAction,
  resellingDisconnectAction,
  resellingDashboardLinkAction,
} from "../../../redux/actions";
import CustomIcons from "../../../assests/CustomIcons";
import LinkedSubAccountsModal from "../../Modals/LinkedSubAccountsModal";
import DeleteConfirmationModal from "../../Modals/DeleteConfirmationModal";

const typographyStyle = {
  fontFamily: "var(--font-family-manrope)",
  fontWeight: 400,
  fontSize: "16px",
};

const formatCentsToCurrency = (cents?: number) => {
  if (cents === undefined || cents === null) return "$0.00";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const ResellingPayments: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [openSubAccountsModal, setOpenSubAccountsModal] =
    useState<boolean>(false);
  const [openDisconnectConfirmModal, setOpenDisconnectConfirmModal] =
    useState<boolean>(false);
  const [isDashboardLinkLoading, setIsDashboardLinkLoading] =
    useState<boolean>(false);

  const {
    accountStatusData,
    accountStatusLoading,
    connectAccountLoading,
    disconnectLoading,
    disconnectErrorData,
  } = useSelector((state: RootState) => state.reselling);

  useEffect(() => {
    dispatch(resellingAccountStatusAction());
  }, [dispatch]);

  const handleConnectStripe = async () => {
    try {
      const res = await dispatch(resellingConnectAccountAction()).unwrap();
      const url = res?.data?.url || res?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Failed to generate connect account link:", err);
    }
  };

  const handleConfirmDisconnectStripe = async () => {
    try {
      await dispatch(resellingDisconnectAction()).unwrap();
      setOpenDisconnectConfirmModal(false);
      dispatch(resellingAccountStatusAction());
    } catch (err: any) {
      setOpenDisconnectConfirmModal(false);
      console.error("Disconnect Stripe failed:", err);
    }
  };

  const handleOpenDashboardLink = async () => {
    try {
      setIsDashboardLinkLoading(true);
      const res = await dispatch(resellingDashboardLinkAction()).unwrap();
      setIsDashboardLinkLoading(false);
      const url = res?.data?.url || res?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setIsDashboardLinkLoading(false);
      console.error("Failed to fetch Stripe Express dashboard link:", err);
    }
  };

  const hasStripeAccount = Boolean(accountStatusData?.stripe_account_id);
  const isActive = accountStatusData?.onboarding_status === "active";
  const isRestricted =
    accountStatusData?.onboarding_status === "restricted" ||
    Boolean(accountStatusData?.disabled_reason);

  const accountName = accountStatusData?.account_name;
  const accountId = accountStatusData?.stripe_account_id || "";

  const isVerified = Boolean(accountStatusData?.is_verified);
  const onboardingStatusLabel = isVerified ? "Verified" : "Unverified";

  const payoutStatusLabel = accountStatusData?.payouts_enabled
    ? "Connected"
    : "Pending";

  const pendingBalance = formatCentsToCurrency(
    accountStatusData?.pending_balance_cents,
  );

  const disabledReason = accountStatusData?.disabled_reason;

  const canDisconnect = accountStatusData?.can_disconnect;
  const blockingSubscriptions = accountStatusData?.blocking_subscriptions || [];

  const disconnectErrorMessage =
    canDisconnect === false
      ? "Cannot disconnect while active resold subscriptions exist"
      : disconnectErrorData?.message ||
        (disconnectErrorData?.data?.active_count
          ? "Cannot disconnect while active resold subscriptions exist"
          : null);

  const subscriptions =
    canDisconnect === false
      ? blockingSubscriptions
      : disconnectErrorData?.data?.subscriptions || blockingSubscriptions;

  const activeCount =
    canDisconnect === false
      ? blockingSubscriptions.length
      : (disconnectErrorData?.data?.active_count ??
        blockingSubscriptions.length);

  return (
    <Box
      sx={{
        maxWidth: "700px",
        marginBottom: "10px",
      }}
    >
      {/* Title & Description Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            ...typographyStyle,
            fontSize: "24px",
            fontFamily: "var(--font-family-manrope)",
            fontWeight: 500,
            color: "#000000",
          }}
        >
          Stripe integration
        </Typography>

        <Typography
          sx={{
            ...typographyStyle,
            color: "#6B7280",
            fontSize: "14px",
            mt: 0.5,
          }}
        >
          Reselling and rebilling are billed to your clients through your own
          Stripe payout account.
        </Typography>
      </Box>

      {accountStatusLoading ? (
        <Box
          sx={{
            p: 3,
            backgroundColor: "#FFF",
            borderRadius: "var(--medium-large-border-radius)",
            border: "1px solid #e5e7eb",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Skeleton width={180} height={28} />
              <Skeleton width={240} height={18} sx={{ mt: 1 }} />
            </Box>
            <Skeleton width={160} height={28} />
          </Box>
          <Divider sx={{ my: 3, border: "1px solid #e5e7eb" }} />
          <Box sx={{ display: "flex", gap: 3 }}>
            <Skeleton width={160} height={36} />
            <Skeleton width={200} height={36} />
          </Box>
        </Box>
      ) : hasStripeAccount ? (
        <>
          {/* Main Stripe Payout Account Card */}
          <Box
            sx={{
              backgroundColor: "#FFF",
              borderRadius: "16px",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              mb: 3,
            }}
          >
            {/* Top Row: Account Name, Verified Chip & Account ID */}
            <Box
              sx={{
                p: "20px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontWeight: 700,
                    fontSize: "18px",
                    color: "#0f172a",
                  }}
                >
                  {accountName}
                </Typography>
                {/* {isDashboardLinkLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CustomIcons.OpenLink
                    onClick={handleOpenDashboardLink}
                    style={{
                      width: "18px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                )} */}
                <Chip
                  label={onboardingStatusLabel}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: "12px",
                    fontFamily: "var(--font-family-manrope)",
                    backgroundColor: isVerified ? "#f0fdf4" : "#fef2f2",
                    color: isVerified ? "#388e3c" : "#991b1b",
                    border: isVerified
                      ? "1px solid #a4d68e"
                      : "1px solid #fca5a5",
                    borderRadius: "9999px",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1.2,
                    },
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily:
                    "monospace, SFMono-Regular, Menlo, Monaco, Consolas",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#1d4ed8",
                  letterSpacing: "0.02em",
                }}
              >
                {accountId}
              </Typography>
            </Box>

            {/* Full-width Horizontal Divider */}
            <Divider sx={{ borderColor: "#e5e7eb" }} />

            {/* Bottom Row: Payouts & Pending Balance Columns */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "stretch",
              }}
            >
              {/* Payouts Column */}
              <Box
                sx={{
                  flex: 1,
                  p: "20px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  borderRight: { xs: "none", sm: "1px solid #e5e7eb" },
                  borderBottom: { xs: "1px solid #e5e7eb", sm: "none" },
                }}
              >
                <Typography
                  sx={{
                    ...typographyStyle,
                    color: "#6B7280",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Payouts
                </Typography>
                <Chip
                  label={payoutStatusLabel}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: "12px",
                    fontFamily: "var(--font-family-manrope)",
                    backgroundColor: accountStatusData?.payouts_enabled
                      ? "#e6f4ea"
                      : "#fef2f2",
                    color: accountStatusData?.payouts_enabled
                      ? "#2e7d32"
                      : "#991b1b",
                    borderRadius: "9999px",
                    height: "24px",
                    "& .MuiChip-label": {
                      px: 1.2,
                    },
                  }}
                />
              </Box>

              {/* Pending Balance Column */}
              <Box
                sx={{
                  flex: 1,
                  p: "20px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    ...typographyStyle,
                    color: "#6B7280",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Pending balance
                </Typography>
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "#1d4ed8",
                  }}
                >
                  {pendingBalance}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Alert Banner for Disconnect Error / Linked Subscriptions */}
          {disconnectErrorMessage && (
            <Box
              sx={{
                p: 2.5,
                backgroundColor: "#FEF2F2",
                border: "1px solid #FCA5A5",
                borderRadius: "var(--medium-large-border-radius)",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 3,
              }}
            >
              <CancelOutlinedIcon
                sx={{
                  color: "#DC2626",
                  fontSize: "20px",
                  mt: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "14px",
                  color: "#991B1B",
                  lineHeight: 1.5,
                }}
              >
                <strong>Cannot disconnect Stripe.</strong>{" "}
                {disconnectErrorMessage}{" "}
                <Box
                  component="span"
                  sx={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    color: "#991B1B",
                    fontWeight: 500,
                    "&:hover": { color: "#7F1D1D" },
                  }}
                  onClick={() => setOpenSubAccountsModal(true)}
                >
                  View linked sub-accounts
                </Box>
              </Typography>
            </Box>
          )}

          {/* Alert Banner for Restricted / Incomplete Onboarding */}
          {(!isActive || disabledReason) && (
            <Box
              sx={{
                p: 2.5,
                backgroundColor: "#FEF2F2",
                border: "1px solid #FCA5A5",
                borderRadius: "var(--medium-large-border-radius)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flex: 1,
                  minWidth: "260px",
                }}
              >
                <CancelOutlinedIcon
                  sx={{
                    color: "#DC2626",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontSize: "14px",
                    color: "#991B1B",
                    lineHeight: 1.5,
                  }}
                >
                  <strong>Stripe Setup Action Required.</strong>{" "}
                  {disabledReason
                    ? `Reason: ${disabledReason}.`
                    : "Your Stripe payout account onboarding is incomplete."}
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={
                  connectAccountLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <LinkIcon sx={{ fontSize: "18px" }} />
                  )
                }
                disabled={connectAccountLoading}
                onClick={handleConnectStripe}
                sx={{
                  backgroundColor: "var(--action-button-background-color)",
                  color: "white",
                  borderRadius: "var(--action-button-radius)",
                  border: "none",
                  padding: "var(--buttons-padding)",
                  fontSize: "var(--medium-body-font-size)",
                  fontFamily: "var(--font-family-manrope)",
                  fontWeight: "400",
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "var(--action-button-background-color)",
                    opacity: 0.9,
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                    cursor: "not-allowed",
                    backgroundColor: "var(--action-button-background-color)",
                    color: "white",
                  },
                }}
              >
                {connectAccountLoading
                  ? "Connecting..."
                  : "Continue Stripe Onboarding"}
              </Button>
            </Box>
          )}

          {/* Action Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              flexWrap: "wrap",
            }}
          >
            <Button
              disabled={
                !isActive || disconnectLoading || canDisconnect === false
              }
              onClick={() => setOpenDisconnectConfirmModal(true)}
              sx={{
                background: "#fff",
                border: "1px solid #d1d5db",
                textTransform: "none",
                fontWeight: 400,
                fontFamily: "var(--font-family-manrope)",
                padding: "8px 14px",
                color: "#000000",
                borderRadius: "var(--large-border-radius)",
                boxShadow: "none",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  color: "#9CA3AF",
                  borderColor: "#E5E7EB",
                  backgroundColor: "#FFFFFF",
                  opacity: 0.8,
                  cursor: "not-allowed",
                },
              }}
            >
              {disconnectLoading ? "Disconnecting..." : "Disconnect Stripe"}
            </Button>
          </Box>
        </>
      ) : (
        /* Disconnected State Card matching Screenshot 1 */
        <Box
          sx={{
            width: "100%",
            p: 4,
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            textAlign: "left",
            boxSizing: "border-box",
          }}
        >
          {/* Credit Card Icon container */}
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              backgroundColor: "#F3F4F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <CreditCardIcon sx={{ fontSize: "20px", color: "#111827" }} />
          </Box>

          <Typography
            sx={{
              ...typographyStyle,
              fontSize: "20px",
              fontWeight: 600,
              color: "#111827",
              mb: 1,
            }}
          >
            Connect your Stripe account
          </Typography>

          <Typography
            sx={{
              ...typographyStyle,
              color: "#6B7280",
              fontSize: "14px",
              lineHeight: 1.5,
              mb: 3,
            }}
          >
            Connect to enable your resale prices and credit markups so clients
            can be billed.
          </Typography>

          <Button
            variant="contained"
            disabled={connectAccountLoading}
            onClick={handleConnectStripe}
            sx={{
              backgroundColor: "var(--action-button-background-color)",
              color: "white",
              borderRadius: "var(--action-button-radius)",
              border: "none",
              padding: "var(--buttons-padding)",
              fontSize: "var(--medium-body-font-size)",
              fontFamily: "var(--font-family-manrope)",
              fontWeight: "400  !important",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                opacity: 0.6,
                cursor: "not-allowed",
              },
            }}
          >
            {connectAccountLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Connecting...</span>
              </Box>
            ) : (
              "Connect Stripe"
            )}
          </Button>
        </Box>
      )}

      {/* Linked Sub-Accounts Modal */}
      <LinkedSubAccountsModal
        open={openSubAccountsModal}
        handleClose={() => setOpenSubAccountsModal(false)}
        subscriptions={subscriptions}
        activeCount={activeCount}
        message={disconnectErrorMessage}
      />

      {/* Disconnect Confirmation Modal */}
      <DeleteConfirmationModal
        open={openDisconnectConfirmModal}
        handleClose={() => setOpenDisconnectConfirmModal(false)}
        onConfirm={handleConfirmDisconnectStripe}
        title="Disconnect Stripe"
        msg="Are you sure you want to disconnect your Stripe account? Active reselling and credit markups will be paused."
        isLoading={disconnectLoading}
        loadingText="Disconnecting..."
      />
    </Box>
  );
};

export default ResellingPayments;
